import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlatformTenantUserList } from '@cloudsign/shared';
import type { PlatformTenantUser } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Badge, Button, Card, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';

type Action = 'resend-invite' | 'password-reset';

/** Users section of the platform tenant detail page (core-phase4 s6a). Reads need platform.tenants_view; actions need platform.tenants_manage. */
export function TenantUsersSection({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const canManage = useCan('platform.tenants_manage');
  const key = ['platform', 'tenants', tenantId, 'users'];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/platform/tenants/${tenantId}/users`, { schema: PlatformTenantUserList }),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: (v: { user: PlatformTenantUser; action: Action }) => api.post(`/platform/tenants/${tenantId}/users/${v.user.userId}/${v.action}`),
    onSuccess: async (_d, v) => {
      setNotice(t(v.action === 'resend-invite' ? 'platformUsers.inviteSent' : 'platformUsers.resetSent', { email: v.user.email }));
      await qc.invalidateQueries({ queryKey: key });
    },
    onError: () => setNotice(null),
  });

  const run = (user: PlatformTenantUser, action: Action) => {
    setNotice(null);
    act.mutate({ user, action });
  };

  return (
    <Card title={t('platformUsers.title')} description={t('platformUsers.description')}>
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      <ErrorLine error={act.error} />
      {notice ? <Alert kind="success">{notice}</Alert> : null}
      {q.data && q.data.items.length === 0 ? <p className="text-slate-600">{t('platformUsers.empty')}</p> : null}
      {q.data && q.data.items.length > 0 ? (
        <TableWrap caption={t('platformUsers.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('platformUsers.email')}</Th>
              <Th>{t('platformUsers.role')}</Th>
              <Th>{t('common.status')}</Th>
              <Th>{t('platformUsers.lastSignIn')}</Th>
              {canManage ? <Th>{t('users.actions')}</Th> : null}
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((u) => {
              const locked = u.lockedUntil !== null && new Date(u.lockedUntil).getTime() > Date.now();
              return (
                <tr key={u.userId} className="border-t border-slate-100 hover:bg-slate-50">
                  <th scope="row" className="px-4 py-3 text-left font-medium">
                    {u.fullName}
                  </th>
                  <td className="px-4 py-3 break-all">{u.email}</td>
                  <td className="px-4 py-3">{u.roleName}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex flex-wrap gap-1">
                      <Badge tone={u.status === 'active' ? 'good' : u.status === 'invited' ? 'warn' : 'neutral'}>{t(`status.${u.status}`)}</Badge>
                      {locked ? <Badge tone="bad">{t('platformUsers.locked')}</Badge> : null}
                    </span>
                  </td>
                  <td className="px-4 py-3">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : t('platformUsers.never')}</td>
                  {canManage ? (
                    <td className="px-4 py-3">
                      {u.status === 'invited' ? (
                        <Button
                          disabled={act.isPending}
                          aria-label={t('platformUsers.resendInviteFor', { name: u.fullName })}
                          onClick={() => run(u, 'resend-invite')}
                        >
                          {t('platformUsers.resendInvite')}
                        </Button>
                      ) : null}
                      {u.status === 'active' ? (
                        <Button
                          disabled={act.isPending}
                          aria-label={t('platformUsers.passwordResetFor', { name: u.fullName })}
                          onClick={() => run(u, 'password-reset')}
                        >
                          {t('platformUsers.passwordReset')}
                        </Button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      ) : null}
    </Card>
  );
}
